// Relative path: src/components/Recommendations/ClinicHighlights.js
import React from 'react';
import laurelRankIcon from '../../images/laurel_rank.svg';
import websiteUtils from '../../utils/WebsiteUtils';

const ClinicHighlights = ({ 
  rating = 4.72,
  reviewCount = 16,
  websiteReviewCounts = {}, // Object with website keys and their counts
  totalReviewCount = 16, // Actual total count (may differ from displayed reviewCount)
  isMobile = false 
}) => {
  // Helper function to get dynamic highlight phrase based on rating
  const getHighlightPhrase = (rating) => {
    const roundedRating = Math.round(rating);
    
    if (roundedRating >= 5) {
      return "Super Clinic";
    } else if (roundedRating >= 4) {
      return "Highly Ranked";
    } else {
      return "Popular Clinic";
    }
  };

  // Helper function to format review count display
  const formatReviewCount = (count) => {
    if (count <= 10) {
      return "10+";
    }
    return count.toString();
  };

  // Helper function to render star rating
  const renderStars = (rating) => {
    const stars = [];
    const roundedRating = Math.round(rating); // Round to nearest whole number for stars display
    const starCount = Math.min(Math.max(roundedRating, 1), 5); // Ensure between 1-5
    
    for (let i = 0; i < 5; i++) {
      if (i < starCount) {
        stars.push(
          <span key={i} className={`text-gray-600 ${isMobile ? 'text-xl' : 'text-2xl'}`}>★</span>
        );
      } else {
        stars.push(
          <span key={i} className={`text-gray-300 ${isMobile ? 'text-xl' : 'text-2xl'}`}>★</span>
        );
      }
    }
    return stars;
  };

  // Helper function to render website review breakdown
  const renderWebsiteBreakdown = () => {
    const websiteKeys = websiteUtils.getWebsiteKeys();
    const displayCount = formatReviewCount(totalReviewCount);
    const isShowingTenPlus = totalReviewCount <= 10;
    const maxPairs = isMobile ? 3 : 5; // Mobile: 3 pairs, Desktop: 4 pairs
    
    // Filter and sort websites by review count (highest first)
    const websitesWithReviews = websiteKeys
      .filter(key => websiteReviewCounts[key] && websiteReviewCounts[key] > 0)
      .map(key => ({ key, count: websiteReviewCounts[key] }))
      .sort((a, b) => b.count - a.count);
    
    if (websitesWithReviews.length === 0) {
      return null;
    }

    let breakdown = [];
    let accountedTotal = 0;

    // Determine how many website slots we can use
    const websiteSlotsAvailable = Math.min(websitesWithReviews.length, maxPairs);
    const needsOtherSlot = websitesWithReviews.length > maxPairs || (isShowingTenPlus && totalReviewCount <= 10);
    const websitesToShow = needsOtherSlot ? Math.min(websitesWithReviews.length, maxPairs - 1) : websiteSlotsAvailable;

    // Add top websites
    for (let i = 0; i < websitesToShow; i++) {
      const { key: websiteKey, count } = websitesWithReviews[i];
      breakdown.push({
        type: 'website',
        websiteKey,
        logo: websiteUtils.getLogo(websiteKey),
        count
      });
      accountedTotal += count;
    }

    // Handle "other" slot if needed
    if (needsOtherSlot) {
      let remainderCount = 0;
      
      if (isShowingTenPlus && totalReviewCount <= 10) {
        // Showing "10+" - calculate remainder to reach 10
        remainderCount = 10 - accountedTotal;
      } else {
        // Normal case - sum remaining websites
        for (let i = websitesToShow; i < websitesWithReviews.length; i++) {
          remainderCount += websitesWithReviews[i].count;
        }
      }
      
      if (remainderCount > 0) {
        breakdown.push({
          type: 'other',
          count: remainderCount,
          showPlus: true
        });
      }
    }

    return (
      <div className={`flex items-center justify-center flex-wrap gap-1 ${isMobile ? 'mt-0' : 'mt-0'}`}>
        {breakdown.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && (
              <span className={`text-gray-400 ${isMobile ? 'text-xs' : 'text-sm'}`}>|</span>
            )}
            <div className="flex items-center">
              {item.type === 'website' ? (
                <>
                  <img 
                    src={item.logo} 
                    alt={websiteUtils.getDisplayName(item.websiteKey)}
                    className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} mr-1`}
                  />
                  <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    {item.count}
                  </span>
                </>
              ) : (
                <>
                  <svg 
                    className={`${isMobile ? 'w-3 h-3' : 'w-4 h-4'} mr-1 text-gray-500`} 
                    fill="currentColor" 
                    viewBox="0 0 20 20"
                  >
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                  <span className={`text-gray-600 font-medium ${isMobile ? 'text-xs' : 'text-sm'}`}>
                    {item.count}{item.showPlus ? '+' : ''}
                  </span>
                </>
              )}
            </div>
          </React.Fragment>
        ))}
      </div>
    );
  };

  // Get dynamic values
  const highlightPhrase = getHighlightPhrase(rating);
  const displayReviewCount = formatReviewCount(totalReviewCount);

  if (isMobile) {
    return (
      <div className="px-5 mt-2">
        <div className="bg-transparent rounded-lg md:border-2 p-2 flex items-center justify-between">
          {/* Highly Ranked Section */}
          <div className="flex-1 flex flex-col items-center text-center">
            <div className="relative flex items-center justify-center mb-1">
              <img 
                src={laurelRankIcon}
                alt={highlightPhrase} 
                className="w-24 h-24"
              />
              <div className="absolute inset-0 flex mt-5 items-start justify-center">
                <div className="text-sm font-bold text-gray-600 text-center leading-tight px-5">
                  {highlightPhrase}
                </div>
              </div>
            </div>
          </div>
          
          {/* Vertical Divider */}
          <div className="w-px h-24 bg-gray-200 mx-1"></div>
          
          {/* Rating Section */}
          <div className="flex-1 flex flex-col items-center text-center">
            <div className="text-2xl md:text-xl font-bold text-gray-600">{rating}</div>
            <div className="flex items-center">
              {renderStars(rating)}
            </div>
          </div>
          
          {/* Vertical Divider */}
          <div className="w-px h-24 bg-gray-200 mx-1"></div>
          
          {/* Reviews Count Section */}
          <div className="flex-1 flex flex-col items-center text-center">
            <div className="text-2xl md:text-xl font-bold text-gray-600 -mb-1">{displayReviewCount}</div>
            <div className="text-sm font-bold text-gray-600 leading-tight">Reviews</div>
            {renderWebsiteBreakdown()}
          </div>
        </div>
      </div>
    );
  }

  // Desktop version
  return (
    <div className="flex bg-transparent rounded-2xl border-2 border-gray-200 overflow-hidden mt-4">
      {/* Highly Ranked Section */}
      <div className="flex-1 px-6 py-0 flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <div className="relative flex items-center justify-center mb-2">
            <img 
              src={laurelRankIcon}
              alt={highlightPhrase} 
              className="w-32 h-32"
            />
            <div className="absolute inset-0 flex mt-9 items-start justify-center">
              <div className="text-base font-extrabold text-gray-700 text-center leading-tight px-4">
                {highlightPhrase}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Vertical Divider */}
      <div className="w-px my-2 bg-gray-200"></div>
      
      {/* Rating Section */}
      <div className="flex-1 px-6 py-4 flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <div className="text-3xl font-black text-gray-600">{rating}</div>
          <div className="flex items-center">
            {renderStars(rating)}
          </div>
        </div>
      </div>
      
      {/* Vertical Divider */}
      <div className="w-px my-2 bg-gray-200"></div>
      
      {/* Reviews Count Section */}
      <div className="flex-1 px-6 py-4 flex items-center justify-center">
        <div className="flex flex-col items-center text-center">
          <div className="text-3xl font-black text-gray-600 -mb-1">{displayReviewCount}</div>
          <div className="text-base font-bold text-gray-600">Reviews</div>
          {renderWebsiteBreakdown()}
        </div>
      </div>
    </div>
  );
};

export default ClinicHighlights;